/**
 * UserType
 */

import React from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { Card } from 'react-bootstrap';
import { FormattedMessage } from 'react-intl';
import { EMPLOYEE_ROLE, PARENT_ROLE, STUDENT_ROLE } from 'containers/User/constants.js';
import CheckMark from 'components/media/Icon2/CheckMark.jsx';
import UserAvatar from 'components/user/UserAvatar/index.jsx';
import SearchInfo from 'components/interaction/Search/SearchInfo.jsx';
import ConnectionIcon from 'components/user/EditAccount/ConnectionIcon.jsx';
import InfoButton from 'components/interaction/InfoButton/index.jsx';
import messages from './messages.js';
import InviteParentsButton from './InviteParentsButton/index.jsx';
import AddEmployeesButton from './AddEmployeesButton/index.jsx';
import AddStudentsButton from './AddStudentsButton/index.jsx';
import ExportUsers from './ExportUsers.jsx';
import AdminList from '../AdminList.jsx';
import useSearchableList from '../useSearchableList.js';
import AdminHeader from '../AdminHeader.jsx';
import { MEMBERTYPE_STAFF } from '../Groups/constants.js';
import AddStaffButton from './AddStaffButton/index.jsx';
import useIsFoundation from '../useIsFoundation.js';

function SsoLogins({ user }) {
  const { externalLogins } = user;
  if (!externalLogins || externalLogins.length === 0) {
    return '-';
  }
  return externalLogins.map(({ id, providerId, email }) => (
    <span key={id} className="d-inline-block text-nowrap">
      <ConnectionIcon providerId={providerId} className="me-2 fs-5" />
      {email}
    </span>
  ));
}

function UserType(props) {
  const { search = '', type } = props;
  const { id, key } = type;
  const isFoundation = useIsFoundation();
  const url = `sa/users?type=${key}`;
  const selector = `sa/users/${id}`;
  const isEmployee = key === EMPLOYEE_ROLE;
  const header = (
    <thead>
      <tr>
        <th className="name"><FormattedMessage {...messages.name} /></th>
        {isEmployee && (
          <>
            <th className="name">
              <FormattedMessage {...messages.ssoConfigured} />
              <InfoButton className="ps-1"><FormattedMessage {...messages.ssoInfo} /></InfoButton>
            </th>
            <th className="name socs-disabled">
              <FormattedMessage {...messages.socsLoginDisabled} />
              <InfoButton className="ps-1"><FormattedMessage {...messages.onlyExternalInfo} /></InfoButton>
            </th>
          </>
        )}
        <th className=""><FormattedMessage {...messages.active} /></th>
        {!isFoundation && key === STUDENT_ROLE && <th className="narrow"><FormattedMessage {...messages.yearClass} /></th>}
        {key === PARENT_ROLE && <th className="narrow"><FormattedMessage {...messages.children} /></th>}
        {key === STUDENT_ROLE && <th className="narrow"><FormattedMessage {...messages.parent} /></th>}
      </tr>
    </thead>
  );
  function renderRow(item) {
    const hasExternalLogins = item.externalLogins?.length > 0;
    return (
      <tr key={item.id}>
        <td className="name">
          <Link to={`/admin/users/${id}/${item.id}`}>
            <UserAvatar user={item} />
          </Link>
        </td>
        {isEmployee && (
          <>
            <td className="text-start">
              <SsoLogins user={item} />
            </td>
            <td className="">
              {hasExternalLogins ? <CheckMark value={item.onlyExternalLogin} falseClass="ss-delete" /> : ''}
            </td>
          </>
        )}
        <td><CheckMark value={Boolean(item.uniqueId)} /></td>
        {!isFoundation && key === STUDENT_ROLE && <td className="narrow">{item.yearClass}</td>}
        {[PARENT_ROLE, STUDENT_ROLE].includes(key) && <td className="narrow">{item.connectionCount || ''}</td>}
      </tr>
    );
  }

  const { items, list } = useSearchableList({ search, url, selector, version: 2, clientSearch: false });
  const control = isFoundation ? (
    key === MEMBERTYPE_STAFF && <AddStaffButton />
  ) : (
    <>
      {key === PARENT_ROLE && <ExportUsers filename="parents.csv" roleType={key} className="me-sm-3 input-height py-2" />}
      {key === PARENT_ROLE && <InviteParentsButton />}
      {key === EMPLOYEE_ROLE && <AddEmployeesButton />}
      {key === STUDENT_ROLE && <AddStudentsButton />}
    </>
  );
  return (
    <Card>
      <AdminHeader
        title={messages[id]}
        control={control}
        count={items.totalCount}
      />
      <SearchInfo className="px-4" search={search} />
      <AdminList
        resetKey={`${search}_${id}`}
        items={items}
        list={list}
        emptyMessage={messages.noUsers}
        header={header}
        renderRow={renderRow}
      />
    </Card>
  );
}

UserType.propTypes = {
  type: PropTypes.object,
  search: PropTypes.string,
};

export default UserType;
